// Google Identity Services wrapper.
//
// Loads the GIS script on demand, exposes initialize + a button
// renderer + a sign-out. The library is small (~20KB), browser-
// only, and modern — drops the older gapi auth surface. All BYOK:
// the Client ID is public by design (OAuth public-client model),
// the ID token JWT we receive is signed by Google and verifiable
// client-side via the embedded certs.
//
// We never send the token anywhere. Identity is local-only unless
// the operator sets VITE_USAGE_ENDPOINT — see usage-ping.ts.

const GIS_SRC = "https://accounts.google.com/gsi/client";

let loadPromise: Promise<void> | null = null;
let initialized = false;

interface GoogleIdResponse {
  credential: string;     // JWT signed by Google (ID token)
  select_by?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (resp: GoogleIdResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    allowed_parent_origin?: string | string[];
  }) => void;
  prompt: () => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
  disableAutoSelect: () => void;
  revoke: (hint: string, done: () => void) => void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleAccountsId } };
  }
}

function loadGisScript(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === "undefined") return reject(new Error("no document"));
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      // Could already be loaded; the polled wait below handles both.
    } else {
      const s = document.createElement("script");
      s.src = GIS_SRC;
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error("Google Identity Services script failed to load"));
      document.head.appendChild(s);
    }
    // GIS sets window.google.accounts.id once ready; poll briefly.
    const start = Date.now();
    const tick = () => {
      if (window.google?.accounts?.id) return resolve();
      if (Date.now() - start > 8000) return reject(new Error("Google Identity Services did not initialize within 8s"));
      setTimeout(tick, 50);
    };
    tick();
  });
  return loadPromise;
}

export interface GoogleUser {
  /** Stable per-account Google subject id. Use this as the primary
   *  identifier; emails can change, sub doesn't. */
  sub: string;
  email: string;
  name: string;
  picture?: string;
  /** Unix epoch seconds. We re-prompt sign-in when this passes. */
  exp: number;
  /** The raw JWT — kept for telemetry callers that want to forward
   *  it server-side for verification. Otherwise unused. */
  credential: string;
}

/** Decode a JWT payload without verifying the signature. Safe because
 *  we never make trust decisions on it — Google signed it, and any
 *  forgery only affects the local user's own UI. Server-side callers
 *  forwarding the credential MUST verify against Google's cert URL. */
function decodeJwtPayload(jwt: string): Record<string, unknown> {
  const parts = jwt.split(".");
  if (parts.length !== 3) throw new Error("malformed JWT");
  const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = payload + "===".slice((payload.length + 3) % 4);
  const json = atob(padded);
  return JSON.parse(json) as Record<string, unknown>;
}

export interface InitializeOptions {
  clientId: string;
  onSignIn: (user: GoogleUser) => void;
}

export async function initializeGoogleAuth(opts: InitializeOptions): Promise<void> {
  await loadGisScript();
  const gid = window.google!.accounts.id;
  gid.initialize({
    client_id: opts.clientId,
    callback: (resp) => {
      try {
        const claims = decodeJwtPayload(resp.credential);
        const user: GoogleUser = {
          sub: String(claims.sub),
          email: String(claims.email ?? ""),
          name: String(claims.name ?? ""),
          picture: typeof claims.picture === "string" ? claims.picture : undefined,
          exp: typeof claims.exp === "number" ? claims.exp : 0,
          credential: resp.credential,
        };
        opts.onSignIn(user);
      } catch (e) {
        console.error("[google-auth] failed to decode credential:", e);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: false,
    // Allow the Playground to be embedded (iframed) on the marketing site so
    // GSI renders its sign-in button inside the cross-origin frame. Standalone
    // (top-level) use is unaffected.
    allowed_parent_origin: ["https://binary-blender.com", "https://www.binary-blender.com"],
  });
  initialized = true;
}

/** Render the official Google "Sign in with Google" button into the
 *  given container. Pass options through to GIS — width is the one
 *  we tend to set. */
export async function renderSignInButton(container: HTMLElement, width = 280): Promise<void> {
  if (!initialized) throw new Error("call initializeGoogleAuth first");
  window.google!.accounts.id.renderButton(container, {
    type: "standard",
    theme: "filled_black",
    size: "large",
    text: "signin_with",
    shape: "rectangular",
    logo_alignment: "left",
    width,
  });
}

/** Local sign-out — disables Google's silent re-pick and clears our
 *  client-side state. Doesn't revoke the grant; user can do that in
 *  their Google account settings if they want to fully detach. */
export function googleSignOut(): void {
  if (initialized) {
    try { window.google!.accounts.id.disableAutoSelect(); } catch { /* ignore */ }
  }
}

// Default Google OAuth client id — same one Agicore Foundry uses.
// OAuth client ids are PUBLIC by design (they ship in every browser
// bundle that uses Google sign-in), so committing this is no leak.
// VITE_GOOGLE_CLIENT_ID overrides it when set, for forks that want
// to run with their own client.
const DEFAULT_CLIENT_ID =
  "746430692055-cqr0vdmuqgl29218ugakhqee5ff9tkp0.apps.googleusercontent.com";

export function googleAuthClientId(): string | undefined {
  const id = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  if (id && id.length > 0) return id;
  return DEFAULT_CLIENT_ID;
}
